// 3×3 matrix helpers and the colour-space matrices of §6.1. Row-major: m[row][col].
export type Vec3 = [number, number, number];
export type Mat3 = [Vec3, Vec3, Vec3];

export function mul(a: Mat3, b: Mat3): Mat3 {
  const r = [0, 1, 2].map((i) =>
    [0, 1, 2].map((j) => a[i]![0] * b[0]![j]! + a[i]![1] * b[1]![j]! + a[i]![2] * b[2]![j]!),
  );
  return r as Mat3;
}

export function apply(m: Mat3, v: Vec3): Vec3 {
  return [
    m[0][0] * v[0] + m[0][1] * v[1] + m[0][2] * v[2],
    m[1][0] * v[0] + m[1][1] * v[1] + m[1][2] * v[2],
    m[2][0] * v[0] + m[2][1] * v[1] + m[2][2] * v[2],
  ];
}

export function inv(m: Mat3): Mat3 {
  const [[a, b, c], [d, e, f], [g, h, i]] = m;
  const A = e * i - f * h;
  const B = -(d * i - f * g);
  const C = d * h - e * g;
  const det = a * A + b * B + c * C;
  if (Math.abs(det) < 1e-15) throw new Error('singular matrix');
  const k = 1 / det;
  return [
    [A * k, -(b * i - c * h) * k, (b * f - c * e) * k],
    [B * k, (a * i - c * g) * k, -(a * f - c * d) * k],
    [C * k, -(a * h - b * g) * k, (a * e - b * d) * k],
  ];
}

export function scale(m: Mat3, s: number): Mat3 {
  return m.map((row) => row.map((v) => v * s)) as Mat3;
}

export const diag = (v: Vec3): Mat3 => [
  [v[0], 0, 0],
  [0, v[1], 0],
  [0, 0, v[2]],
];

export const IDENTITY: Mat3 = diag([1, 1, 1]);

/** Column-major Float32Array for WebGL `uniformMatrix3fv(loc, false, …)`. */
export function toGl(m: Mat3): Float32Array {
  return new Float32Array([m[0][0], m[1][0], m[2][0], m[0][1], m[1][1], m[2][1], m[0][2], m[1][2], m[2][2]]);
}

// ── §6.1 RGB → XYZ (D65) ─────────────────────────────────────────────
export const SRGB_TO_XYZ: Mat3 = [
  [0.4124564, 0.3575761, 0.1804375],
  [0.2126729, 0.7151522, 0.072175],
  [0.0193339, 0.119192, 0.9503041],
];
export const P3_TO_XYZ: Mat3 = [
  [0.4865709, 0.2656677, 0.1982173],
  [0.2289746, 0.6917385, 0.0792869],
  [0.0, 0.0451134, 1.0439444],
];
export const ADOBE_TO_XYZ: Mat3 = [
  [0.5767309, 0.185554, 0.1881852],
  [0.2973769, 0.6273491, 0.0752741],
  [0.0270343, 0.0706872, 0.9911085],
];
export const XYZ_TO_SRGB = inv(SRGB_TO_XYZ);
export const XYZ_TO_P3 = inv(P3_TO_XYZ);
export const XYZ_TO_ADOBE = inv(ADOBE_TO_XYZ);

export const P3_TO_SRGB = mul(XYZ_TO_SRGB, P3_TO_XYZ);
export const SRGB_TO_P3 = mul(XYZ_TO_P3, SRGB_TO_XYZ);
export const P3_TO_ADOBE = mul(XYZ_TO_ADOBE, P3_TO_XYZ);
export const ADOBE_TO_P3 = mul(XYZ_TO_P3, ADOBE_TO_XYZ);

export type ColorSpaceId = 'srgb' | 'p3' | 'adobergb';
export const TO_XYZ: Record<ColorSpaceId, Mat3> = {
  srgb: SRGB_TO_XYZ,
  p3: P3_TO_XYZ,
  adobergb: ADOBE_TO_XYZ,
};

/** Working space (linear P3) → output space (linear). */
export function workingTo(space: ColorSpaceId): Mat3 {
  return space === 'p3' ? IDENTITY : space === 'srgb' ? P3_TO_SRGB : P3_TO_ADOBE;
}
/** Source space (linear) → working space (linear P3). */
export function toWorking(space: ColorSpaceId): Mat3 {
  return space === 'p3' ? IDENTITY : space === 'srgb' ? SRGB_TO_P3 : ADOBE_TO_P3;
}

/** §6.1 luminance in the working space. */
export const lumaP3 = (r: number, g: number, b: number) => 0.2289746 * r + 0.6917385 * g + 0.0792869 * b;
